/**
 * Renders a platform subscription invoice PDF.
 * HTML + html2canvas for brand colors, logos, and real layout.
 */

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import type { PlatformInvoice } from '@ruta/shared';
import { formatCurrency, parseDate } from '@/lib/utils';
import { STONE, resolveCssColorHex } from '@/lib/brandColors';

/** Forest Prompt wordmark, generated by tools/brand (email uses its own 320px render of the same mark). */
const RUTA_WORDMARK_URL = '/ruta-wordmark.png';

async function toDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Paint a monogram as a PNG so html2canvas doesn't fight CSS text metrics /
 * flex / table-cell centering (all of which land the glyph off-center in PDF).
 */
function monogramDataUrl(letter: string, bg: string, size = 40): string {
  const dpr = 2;
  const canvas = document.createElement('canvas');
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.scale(dpr, dpr);

  const radius = 8;
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.arcTo(size, 0, size, size, radius);
  ctx.arcTo(size, size, 0, size, radius);
  ctx.arcTo(0, size, 0, 0, radius);
  ctx.arcTo(0, 0, size, 0, radius);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.font = `700 15px Prompt, system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Optical nudge: capital glyphs often sit slightly high with true middle baseline.
  ctx.fillText(letter.toUpperCase().slice(0, 1), size / 2, size / 2 + 0.5);

  return canvas.toDataURL('image/png');
}

function periodLabel(invoice: PlatformInvoice): string | null {
  if (!invoice.periodStart || !invoice.periodEnd) return null;
  return `${format(parseDate(invoice.periodStart), 'MMM d, yyyy')} – ${format(parseDate(invoice.periodEnd), 'MMM d, yyyy')}`;
}

function metaRow(label: string, value: string, stone: typeof STONE): string {
  return `
    <div style="display:flex;justify-content:flex-end;align-items:baseline;gap:16px;margin-bottom:8px;">
      <span style="font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;color:${stone[400]};">${label}</span>
      <span style="font-size:13px;font-weight:500;color:${stone[800]};font-variant-numeric:tabular-nums;min-width:148px;text-align:right;">${value}</span>
    </div>`;
}

function buildHtml(
  invoice: PlatformInvoice,
  opts: {
    nav: string;
    primary: string;
    rutaWordmark: string | null;
    companyLogo: string | null;
    monogram: string | null;
  },
): string {
  const { nav, primary, rutaWordmark, companyLogo, monogram } = opts;
  const period = periodLabel(invoice);
  const billed = format(new Date(invoice.billedAt), 'MMM d, yyyy');
  const typeLabel = invoice.billingType === 'activation' ? 'Activation' : 'Monthly';
  const statusLabel =
    invoice.status === 'paid' ? 'Paid' : invoice.status === 'open' ? 'Open' : invoice.status;
  const statusColor = invoice.status === 'paid' ? primary : STONE[600];

  const lineRows = invoice.lineItems
    .map((line, i, arr) => {
      const isLast = i === arr.length - 1;
      const border = isLast ? 'none' : `1px solid ${STONE[100]}`;
      const qty =
        line.quantity != null
          ? `${line.quantity.toLocaleString()}${line.unitPrice != null ? ` × ${formatCurrency(line.unitPrice)}` : ''}`
          : '—';
      return `
        <tr>
          <td style="padding:11px 0;border-bottom:${border};font-size:13px;color:${STONE[800]};font-weight:500;vertical-align:baseline;">
            ${escapeHtml(line.description)}
          </td>
          <td style="padding:11px 0 11px 20px;border-bottom:${border};font-size:12px;color:${STONE[400]};text-align:right;white-space:nowrap;vertical-align:baseline;font-variant-numeric:tabular-nums;">
            ${escapeHtml(qty)}
          </td>
          <td style="padding:11px 0 11px 24px;border-bottom:${border};font-size:13px;color:${STONE[800]};text-align:right;font-variant-numeric:tabular-nums;font-weight:600;white-space:nowrap;vertical-align:baseline;width:110px;">
            ${formatCurrency(line.amount)}
          </td>
        </tr>`;
    })
    .join('');

  const paymentBlock = invoice.paymentSummary
    ? `
    <div style="margin-top:24px;border-radius:10px;background:${STONE[50]};border:1px solid ${STONE[200]};padding:16px 18px;">
      <div style="font-size:10px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:${STONE[400]};margin-bottom:12px;">
        Customer payments this period
      </div>
      <table style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="padding:0 16px 0 0;vertical-align:top;width:33%;">
            <div style="font-size:11px;color:${STONE[500]};margin-bottom:3px;">Volume</div>
            <div style="font-size:17px;font-weight:700;color:${STONE[900]};font-variant-numeric:tabular-nums;letter-spacing:-0.02em;">
              ${formatCurrency(invoice.paymentSummary.transactionVolume)}
            </div>
          </td>
          <td style="padding:0 16px;vertical-align:top;width:33%;">
            <div style="font-size:11px;color:${STONE[500]};margin-bottom:3px;">Fees paid</div>
            <div style="font-size:17px;font-weight:700;color:${STONE[900]};font-variant-numeric:tabular-nums;letter-spacing:-0.02em;">
              ${formatCurrency(invoice.paymentSummary.feesPaid)}
            </div>
          </td>
          <td style="padding:0 0 0 16px;vertical-align:top;width:33%;">
            <div style="font-size:11px;color:${STONE[500]};margin-bottom:3px;">Transactions</div>
            <div style="font-size:17px;font-weight:700;color:${STONE[900]};font-variant-numeric:tabular-nums;letter-spacing:-0.02em;">
              ${invoice.paymentSummary.transactionCount.toLocaleString()}
            </div>
          </td>
        </tr>
      </table>
      <div style="margin-top:12px;padding-top:10px;border-top:1px solid ${STONE[200]};font-size:11px;color:${STONE[500]};line-height:1.45;">
        Your rates: ${escapeHtml(invoice.paymentSummary.feeRateReminder)}
      </div>
    </div>`
    : '';

  return `
<div id="platform-invoice-root" style="
  width:800px;
  box-sizing:border-box;
  background:#ffffff;
  color:${STONE[800]};
  font-family:'Prompt',system-ui,-apple-system,BlinkMacSystemFont,sans-serif;
  -webkit-font-smoothing:antialiased;
">
  <div style="height:5px;background:${nav};"></div>

  <div style="padding:36px 44px 40px;">
    <!-- Header: wordmark + product · Invoice title -->
    <div style="display:flex;justify-content:space-between;align-items:center;gap:20px;margin-bottom:28px;">
      <div style="display:flex;align-items:center;gap:12px;min-width:0;">
        ${
          rutaWordmark
            ? `<img src="${rutaWordmark}" alt="Ruta" style="height:22px;width:auto;display:block;" />`
            : `<div style="font-size:20px;font-weight:900;letter-spacing:0.14em;color:${nav};">RUTA</div>`
        }
        <div style="width:1px;height:18px;background:${STONE[200]};flex-shrink:0;"></div>
        <div style="font-size:10px;font-weight:600;letter-spacing:0.14em;text-transform:uppercase;color:${STONE[400]};white-space:nowrap;">
          Platform subscription
        </div>
      </div>
      <div style="text-align:right;flex-shrink:0;">
        <div style="font-size:24px;font-weight:700;letter-spacing:-0.03em;color:${nav};line-height:1;">Invoice</div>
        <div style="margin-top:4px;font-size:11px;font-variant-numeric:tabular-nums;color:${STONE[500]};letter-spacing:0.02em;">
          ${escapeHtml(invoice.invoiceNumber)}
        </div>
      </div>
    </div>

    <!-- Prepared for · meta (aligned rows) -->
    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:40px;margin-bottom:28px;padding-bottom:22px;border-bottom:1px solid ${STONE[200]};">
      <div style="min-width:0;flex:1;">
        <div style="font-size:10px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:${STONE[400]};margin-bottom:10px;">
          Prepared for
        </div>
        <div style="display:flex;align-items:center;gap:12px;">
          ${
            companyLogo
              ? `<div style="width:40px;height:40px;border-radius:8px;border:1px solid ${STONE[200]};background:${STONE[50]};overflow:hidden;flex-shrink:0;display:flex;align-items:center;justify-content:center;">
                  <img src="${companyLogo}" alt="" width="32" height="32" style="width:32px;height:32px;object-fit:contain;display:block;" />
                </div>`
              : monogram
                ? `<img src="${monogram}" alt="" width="40" height="40" style="width:40px;height:40px;display:block;flex-shrink:0;border-radius:8px;" />`
                : `<div style="width:40px;height:40px;border-radius:8px;background:${primary};flex-shrink:0;"></div>`
          }
          <div style="min-width:0;">
            <div style="font-size:15px;font-weight:600;color:${STONE[900]};letter-spacing:-0.01em;line-height:1.25;">
              ${escapeHtml(invoice.organizationName || 'Organization')}
            </div>
            <div style="margin-top:2px;font-size:12px;color:${STONE[500]};">${typeLabel} billing</div>
          </div>
        </div>
      </div>

      <div style="flex-shrink:0;padding-top:2px;">
        ${metaRow('Billed', billed, STONE)}
        ${period ? metaRow('Period', escapeHtml(period), STONE) : ''}
        ${metaRow(
          'Status',
          `<span style="color:${statusColor};font-weight:600;">${escapeHtml(statusLabel)}</span>`,
          STONE,
        )}
      </div>
    </div>

    <!-- Charges -->
    <div style="font-size:10px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:${STONE[400]};margin-bottom:6px;">
      Charges
    </div>
    <table style="width:100%;border-collapse:collapse;">
      <thead>
        <tr>
          <th style="text-align:left;padding:0 0 8px;font-size:10px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:${STONE[400]};border-bottom:1px solid ${STONE[200]};">Description</th>
          <th style="text-align:right;padding:0 0 8px 20px;font-size:10px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:${STONE[400]};border-bottom:1px solid ${STONE[200]};">Detail</th>
          <th style="text-align:right;padding:0 0 8px 24px;font-size:10px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:${STONE[400]};border-bottom:1px solid ${STONE[200]};width:110px;">Amount</th>
        </tr>
      </thead>
      <tbody>${lineRows}</tbody>
    </table>

    <!-- Total — amount column aligned to table amount col -->
    <div style="display:flex;justify-content:flex-end;margin-top:4px;padding-top:14px;border-top:1px solid ${STONE[200]};">
      <table style="border-collapse:collapse;">
        <tr>
          <td style="padding:0 24px 0 0;font-size:11px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;color:${STONE[500]};vertical-align:baseline;text-align:right;">
            ${invoice.status === 'paid' ? 'Total paid' : 'Total due'}
          </td>
          <td style="padding:0;font-size:24px;font-weight:700;color:${nav};font-variant-numeric:tabular-nums;letter-spacing:-0.03em;vertical-align:baseline;text-align:right;width:110px;white-space:nowrap;">
            ${formatCurrency(invoice.total)}
          </td>
        </tr>
      </table>
    </div>

    ${paymentBlock}

    <!-- Footer -->
    <div style="margin-top:28px;padding-top:14px;border-top:1px solid ${STONE[100]};display:flex;justify-content:space-between;align-items:flex-end;gap:16px;">
      <div style="font-size:11px;color:${STONE[400]};line-height:1.5;">
        Thank you for building with Ruta.
        ${invoice.chargeId ? `<br/><span style="font-variant-numeric:tabular-nums;font-size:10px;">Payment ref ${escapeHtml(invoice.chargeId)}</span>` : ''}
      </div>
      <div style="font-size:10px;font-weight:600;letter-spacing:0.08em;color:${STONE[300]};">
        getruta.com
      </div>
    </div>
  </div>
</div>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export async function downloadPlatformInvoicePdf(
  invoice: PlatformInvoice,
  options?: { companyLogoUrl?: string | null },
): Promise<void> {
  const nav = resolveCssColorHex('--nav');
  const primary = resolveCssColorHex('--primary');
  const navHex = nav.startsWith('#') ? nav : '#0a1f0a';
  const primaryHex = primary.startsWith('#') ? primary : '#486948';
  const letter = (invoice.organizationName || 'C').charAt(0).toUpperCase();
  const [rutaWordmark, companyLogo] = await Promise.all([
    toDataUrl(RUTA_WORDMARK_URL),
    options?.companyLogoUrl ? toDataUrl(options.companyLogoUrl) : Promise.resolve(null),
  ]);
  // Wait a tick so Prompt is available for canvas text measurement when possible.
  await document.fonts?.ready?.catch?.(() => undefined);
  const monogram = companyLogo ? null : monogramDataUrl(letter, primaryHex);

  const host = document.createElement('div');
  host.style.cssText =
    'position:fixed;left:-10000px;top:0;width:800px;pointer-events:none;opacity:1;z-index:-1;';
  host.innerHTML = buildHtml(invoice, {
    nav: navHex,
    primary: primaryHex,
    rutaWordmark,
    companyLogo,
    monogram,
  });
  document.body.appendChild(host);

  const root = host.querySelector('#platform-invoice-root') as HTMLElement;
  try {
    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) resolve();
            else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          }),
      ),
    );

    const canvas = await html2canvas(root, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: 800,
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });
    const pageWidth = 8.5;
    const pageHeight = 11;
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/jpeg', 0.96);

    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`${invoice.invoiceNumber}.pdf`);
  } finally {
    document.body.removeChild(host);
  }
}
